/**
 * **Card 4 — Payment** of the booking-flow editor (C25, FR-J11;
 * [ADR-0052](../../../../../../docs/adr/0052-a-booking-may-be-paid-by-bank-qr-and-staff-confirm-the-payment.md)
 * §1; Wave 27C, T-5.77). Wave 20's *Take payment online* switch becomes **one
 * payment method per flow** — none, the firm's gateway, or a bank transfer to
 * the firm's QR — because a firm that wants both builds two flows (the owner's
 * choice).
 *
 * A **bank-QR** flow carries:
 *
 * - **the QR picture**, chosen from the firm's own Pictures through
 *   `MediaBrowser` in pick mode (an upload there *is* the choice). It must be a
 *   `/site-media/` picture — the service refuses anything else — and while a
 *   flow uses it, Pictures will not delete it (`domain/site_media._sources`);
 * - **payee text**, each optional — UPI ID, account name, account number, IFSC,
 *   bank — shown beside the QR with the exact amount and **read live**, so a
 *   firm that changes its bank is never paid into the old one. The UPI and IFSC
 *   shapes are the service's own checks (`validate_payment_qr`), said here in
 *   its words before Save rather than after it;
 * - ***Payment proof required*** — whether the visitor must send a screenshot;
 * - the two QR wording texts (`QR_WORDING_KEYS`): the button that sends the
 *   payment details, and what the page says while the firm checks them.
 *
 * **A QR flow cannot go live without its picture** (`rule = qr_needs_image`):
 * the edit bar says so and Save waits. The QR and the payee text are **kept
 * whatever the method** (27A, I-1), so switching a flow to the gateway and back
 * loses nothing the firm typed.
 */

import { Button, Card, CardBody, Dialog, Input, RadioGroup, Textarea } from '@fiveox/ui'
import { QrCode } from 'lucide-react'
import { useState } from 'react'

import { FORM_GRID, FormRow } from '../../../components/page'
import {
  type FlowBody,
  type FlowVocabulary,
  PAYEE_FIELDS,
  PAYMENT_MODE_LABELS,
  PAYMENT_MODES,
  type PayeeField,
  type PaymentMode,
  type PaymentQr,
  QR_WORDING_KEYS,
  type QrWordingKey,
} from '../../../lib/api/booking'
import { MediaBrowser } from '../fields/MediaLibrary'
import { CardHead, SwitchRow } from './cardParts'

// -- the words -----------------------------------------------------------------

/** What each method does, under its name in the radio group. */
export const PAYMENT_MODE_NOTES: Record<PaymentMode, string> = {
  none: 'No payment on the page. The booking comes to you, and you invoice as you usually do.',
  gateway: 'They pay the plan’s price through your payment gateway. The booking is confirmed when the payment arrives.',
  qr: 'They pay the exact amount to your bank QR and send the payment details. You check the payment and confirm the booking in Bookings — the invoice is issued then.',
}

export const PAYEE_NAMES: Record<PayeeField, { label: string; placeholder: string }> = {
  upi_id: { label: 'UPI ID', placeholder: 'yourfirm@okhdfcbank' },
  account_name: { label: 'Account name', placeholder: 'The name on the account' },
  account_number: { label: 'Account number', placeholder: '50100123456789' },
  ifsc: { label: 'IFSC', placeholder: 'HDFC0001234' },
  bank_name: { label: 'Bank', placeholder: 'HDFC Bank, Baner branch' },
}

/** The service's own limits (`PAYEE_FIELDS` in `app/domain/booking.py`), for a
 *  vocabulary from before C25. */
const PAYEE_MAX: Record<PayeeField, number> = {
  upi_id: 100,
  account_name: 120,
  account_number: 34,
  ifsc: 11,
  bank_name: 120,
}

export const QR_WORDING_NAMES: Record<QrWordingKey, { label: string; hint: string }> = {
  submit_proof: { label: 'Send payment button', hint: 'Sends their payment details once they have paid.' },
  checking: { label: 'While you check', hint: 'What they read once they have sent them, until you confirm the payment.' },
}

/** The service's sentences (`app/domain/booking.py`), so the edit bar and the
 *  refusal after Save say the same thing. */
export const UPI_SHAPE = 'A UPI ID looks like name@bank.'
export const IFSC_SHAPE = 'An IFSC is eleven characters: four letters, a zero, then six letters or digits.'
export const QR_NEEDS_IMAGE = 'A flow paid by bank QR needs its QR picture before it can go live.'

/** The service's two shapes (`UPI_ID`, `IFSC`) — 27A's, kept identical. */
const UPI = /^[A-Za-z0-9._-]{2,256}@[A-Za-z][A-Za-z0-9.-]{1,63}$/
const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/

export const EMPTY_QR: PaymentQr = {
  image_url: null,
  upi_id: null,
  account_name: null,
  account_number: null,
  ifsc: null,
  bank_name: null,
}

// -- the rules, before Save ------------------------------------------------------

/** What is wrong with one payee field, in the service's words — `null` when
 *  it is empty (each is optional) or well-formed. */
export function payeeProblem(field: PayeeField, value: string | null | undefined): string | null {
  const text = (value ?? '').trim()
  if (!text) return null
  if (field === 'upi_id' && !UPI.test(text)) return UPI_SHAPE
  if (field === 'ifsc' && !IFSC.test(text.toUpperCase().replace(/\s/g, ''))) return IFSC_SHAPE
  return null
}

/** The QR as Save sends it: all six keys, each trimmed, an empty one `null`,
 *  the IFSC upper-cased without spaces (as the service stores it) — and `null`
 *  for a QR with nothing in it, so a draft that typed and cleared a field is
 *  not *changed*. */
export function cleanPaymentQr(qr: Partial<PaymentQr> | null | undefined): PaymentQr | null {
  if (!qr) return null
  const out: PaymentQr = { ...EMPTY_QR, image_url: qr.image_url?.trim() || null }
  for (const field of PAYEE_FIELDS) {
    let text = (qr[field] ?? '').trim()
    if (field === 'ifsc') text = text.toUpperCase().replace(/\s/g, '')
    out[field] = text || null
  }
  return Object.values(out).some((value) => value !== null) ? out : null
}

/** What stops Save on the payment card. The payee text is checked whatever the
 *  method, because the service keeps it — and checks it — whatever the method. */
export function paymentProblems(body: Pick<FlowBody, 'status' | 'payment_mode' | 'payment_qr'>): string[] {
  const out: string[] = []
  for (const field of PAYEE_FIELDS) {
    const problem = payeeProblem(field, body.payment_qr?.[field])
    if (problem) out.push(problem)
  }
  if (body.status === 'live' && body.payment_mode === 'qr' && !body.payment_qr?.image_url) out.push(QR_NEEDS_IMAGE)
  return out
}

// -- the card ----------------------------------------------------------------------

export function PaymentCard({
  n,
  values,
  vocab,
  editable,
  mediaEditable,
  onChange,
}: {
  n: number
  values: FlowBody
  vocab: FlowVocabulary
  editable: boolean
  /** `site:can_update` — whether the picker may upload a new picture. Without
   *  it the firm's existing pictures can still be chosen. */
  mediaEditable: boolean
  onChange: (patch: Partial<FlowBody>) => void
}) {
  const mode: PaymentMode = values.payment_mode ?? (values.payment ? 'gateway' : 'none')
  const qr = values.payment_qr ?? EMPTY_QR
  const [picking, setPicking] = useState(false)
  const [touched, setTouched] = useState<Set<PayeeField>>(() => new Set())
  const setQr = (patch: Partial<PaymentQr>) => onChange({ payment_qr: { ...qr, ...patch } })
  const modes = vocab.payment_modes?.length ? vocab.payment_modes : PAYMENT_MODES
  const proofRequired = values.proof_required ?? true
  const qrWording = QR_WORDING_KEYS.filter((key) => vocab.wording[key])

  return (
    <Card data-card="payment">
      <CardBody className="space-y-4">
        <CardHead n={n} title="Payment" note="How they pay once they have chosen — or whether they pay here at all." />
        <div data-payment-mode={mode}>
          <RadioGroup<PaymentMode>
            legend="How they pay"
            hideLegend
            value={mode}
            onChange={(next) => editable && onChange({ payment_mode: next, payment: next !== 'none' })}
            options={modes.map((value) => ({
              value,
              label: PAYMENT_MODE_LABELS[value],
              description: PAYMENT_MODE_NOTES[value],
              disabled: !editable,
            }))}
          />
        </div>

        {mode === 'qr' && (
          <div className="border-border space-y-5 rounded-lg border p-3 sm:p-4" data-qr="">
            <div className="flex flex-wrap items-start gap-4" data-qr-picture={qr.image_url ? 'set' : 'none'}>
              {qr.image_url ? (
                <img
                  src={qr.image_url}
                  alt="Your payment QR"
                  width={128}
                  height={128}
                  className="border-border h-32 w-32 rounded-lg border bg-white object-contain"
                  data-qr-image=""
                />
              ) : (
                <div className="border-border text-muted-foreground flex h-32 w-32 shrink-0 items-center justify-center rounded-lg border border-dashed">
                  <QrCode size={28} aria-hidden />
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-foreground text-sm font-medium">QR picture</p>
                <p className="text-muted-foreground text-xs">
                  {qr.image_url
                    ? 'They scan this to pay, beside the exact amount. It comes from your website’s Pictures, which keep it while this flow uses it.'
                    : 'Your bank’s QR code, from your website’s Pictures — upload it there or here. A flow paid by bank QR needs it before it can go live.'}
                </p>
                {!qr.image_url && values.status === 'live' && (
                  <p className="text-destructive text-xs" data-qr-missing="">
                    {QR_NEEDS_IMAGE}
                  </p>
                )}
                {editable && (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setPicking(true)} data-choose-qr="">
                      {qr.image_url ? 'Change the QR' : 'Choose the QR'}
                    </Button>
                    {qr.image_url && (
                      <Button variant="ghost" size="sm" onClick={() => setQr({ image_url: null })} data-remove-qr="">
                        Take it off this flow
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-foreground text-sm font-medium">Payee details</p>
              <p className="text-muted-foreground text-xs">
                Shown beside the QR, so they can also pay by bank transfer. Each is optional.
              </p>
              <div className={FORM_GRID} data-payee="">
                {PAYEE_FIELDS.map((field) => {
                  const id = `payee-${field}`
                  return (
                    <FormRow key={field} label={PAYEE_NAMES[field].label} htmlFor={id} optional>
                      <Input
                        id={id}
                        value={qr[field] ?? ''}
                        maxLength={vocab.payee_fields?.[field]?.max ?? PAYEE_MAX[field]}
                        placeholder={PAYEE_NAMES[field].placeholder}
                        disabled={!editable}
                        autoComplete="off"
                        error={touched.has(field) ? payeeProblem(field, qr[field]) : null}
                        data-payee-field={field}
                        onBlur={() => setTouched((prev) => new Set(prev).add(field))}
                        onChange={(e) => setQr({ [field]: e.target.value })}
                      />
                    </FormRow>
                  )
                })}
              </div>
            </div>

            <SwitchRow
              label="Payment proof required"
              note={
                proofRequired
                  ? 'They must send a screenshot of the payment to finish their booking.'
                  : 'A screenshot is optional — they can send the payment reference instead.'
              }
              name="proof_required"
              checked={proofRequired}
              disabled={!editable}
              onChange={(on) => onChange({ proof_required: on })}
            />

            {qrWording.length > 0 && (
              <div className={FORM_GRID} data-qr-wording="">
                {qrWording.map((key) => {
                  const spec = vocab.wording[key]
                  const id = `wording-${key}`
                  const value = values.wording[key] ?? ''
                  const change = (text: string) => onChange({ wording: { ...values.wording, [key]: text } })
                  return (
                    <FormRow key={key} label={QR_WORDING_NAMES[key].label} htmlFor={id}>
                      {key === 'checking' ? (
                        <Textarea
                          id={id}
                          rows={3}
                          maxLength={spec?.max}
                          value={value}
                          placeholder={spec?.default}
                          disabled={!editable}
                          hint={QR_WORDING_NAMES[key].hint}
                          data-wording={key}
                          onChange={(e) => change(e.target.value)}
                        />
                      ) : (
                        <Input
                          id={id}
                          maxLength={spec?.max}
                          value={value}
                          placeholder={spec?.default}
                          disabled={!editable}
                          hint={QR_WORDING_NAMES[key].hint}
                          data-wording={key}
                          onChange={(e) => change(e.target.value)}
                        />
                      )}
                    </FormRow>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <SwitchRow
          label="Open a case when booked"
          note="A case for the chosen service, opened when the booking is confirmed."
          name="create_case"
          checked={values.create_case}
          disabled={!editable}
          onChange={(on) => onChange({ create_case: on })}
        />

        <Dialog open={picking} onClose={() => setPicking(false)} title="Choose your payment QR" width="xl">
          {picking ? (
            <MediaBrowser
              mode="pick"
              editable={mediaEditable}
              onPick={(item) => {
                setQr({ image_url: item.url })
                setPicking(false)
              }}
            />
          ) : null}
        </Dialog>
      </CardBody>
    </Card>
  )
}
